import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSeo, { BASE } from "@/components/feature/PageSeo";

const principles = [
  { icon: "ri-eye-line", title: "真实优先", desc: "坚持多方核对可公开来源，还原走红事件真实脉络，不编造、不夸大、不消费当事人。" },
  { icon: "ri-scales-3-line", title: "客观克制", desc: "对仍在变化的热点标注追踪状态，区分事实与评论，给读者可判断的依据。" },
  { icon: "ri-focus-3-line", title: "关注小人物", desc: "不追八卦猎奇，更在意素人到主角的那条出圈之路——专注本身值得被记录。" },
  { icon: "ri-archive-line", title: "持续沉淀", desc: "把每一次走红做成可回看的档案，让热点不只是一阵风，而是有价值的内容沉淀。" },
];

const voices = [
  {
    icon: "ri-cup-line",
    text: "这里讲得走红故事很“素”，却能让我真实地代入。下班路上看一篇返乡陶娘的视频复盘，整个人都慢下来了。",
    who: "一位每天追更的读者",
  },
  {
    icon: "ri-fire-line",
    text: "终于有个地方不靠标题党也能看懂人为什么火。每篇复盘都会告诉我走红到底做对了什么，不是单纯看热闹。",
    who: "从事内容行业的读者",
  },
  {
    icon: "ri-leaf-line",
    text: "关注吃瓜爆网之后，我才知道手艺人和县城理发师的故事也能这么有力量。感谢你们愿意记录这些小人物。",
    who: "一位老读者",
  },
];

export default function About() {
  return (
    <>
      <PageSeo
        title="关于我们｜吃瓜爆网品牌介绍与内容定位"
        description="了解吃瓜爆网：一个专注于新晋网红与爆红人物走红事件追踪的内容平台，坚持真实、客观、关注小人物，把每一次素人出圈做成有温度的档案。"
        keywords="吃瓜爆网,关于我们,品牌介绍,内容原则,走红事件追踪,爆红人物档案"
        path="/about"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于吃瓜爆网",
            url: BASE + "/about",
            about: {
              "@type": "Organization",
              name: "吃瓜爆网",
              description: "专注新晋网红与爆红人物走红事件追踪的内容平台",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系吃瓜爆网",
            url: BASE + "/about",
          },
        ]}
      />
      <div className="min-h-screen bg-background-50">
        <Header />
        <main>
          <section className="border-b border-background-200 bg-background-100">
            <div className="container-x py-10 md:py-14">
              <span className="eyebrow">
                <i className="ri-building-2-line" aria-hidden="true" />
                关于我们
              </span>
              <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
                我们记录每一次<br className="hidden md:block" />认真生活的人被看见
              </h1>
              <p className="mt-5 max-w-2xl text-[15px] leading-7 text-foreground-700 md:text-base">
                吃瓜爆网是一个以简体中文运营的娱乐资讯内容站，持续追踪新晋网红与爆红人物的走红事件与热门主角。我们相信，出圈的故事里藏着这个时代真实、鲜活的生活样本。
              </p>
            </div>
            <Breadcrumb items={[{ label: "关于我们" }]} />
          </section>

          <section className="container-x py-12 md:py-16">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div className="relative order-2 overflow-hidden rounded-lg bg-background-200 lg:order-1 lg:aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20editorial%20illustration%20of%20a%20small%20editorial%20team%20of%20diverse%20Chinese%20people%20reading%20folded%20papers%20and%20discussing%20stories%20around%20a%20warm%20desk%20lamp%2C%20textured%20gouache%20on%20cream%20paper%2C%20muted%20terracotta%20amber%20and%20ink%20palette%2C%20magazine%20cover%20art%2C%20cozy%20generous%20negative%20space&width=1200&height=900&seq=about-editorial-01&orientation=landscape"
                  alt="吃瓜爆网编辑部选稿与记录故事的过程插画"
                  title="吃瓜爆网编辑部"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="order-1 lg:order-2">
                <span className="eyebrow">
                  <i className="ri-rocket-2-line" aria-hidden="true" />
                  我们是谁
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                  品牌介绍与定位
                </h2>
                <p className="mt-4 text-[15px] leading-8 text-foreground-800">
                  吃瓜爆网定位为“走红事件的非虚构档案站”。我们不追黑料、不贴标签，而是把注意力放在一件事上：一个普通人，是如何通过一次真实的表达、一门扎实的手艺或一段朴素的生活，被越来越多的人看见。
                </p>
                <p className="mt-3 text-[15px] leading-8 text-foreground-800">
                  网站围绕“走红事件”“热门主角”“出圈复盘”三条主线组织内容，覆盖才艺破圈、手作非遗、职业叙事、萌宠治愈、表达共鸣等常见走红类型，为读者提供既好看又能读懂的追更体验。
                </p>
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div>
                    <p className="font-heading text-2xl font-bold text-primary-500">5+</p>
                    <p className="mt-1 text-xs text-foreground-600">走红栏目</p>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-primary-500">日更</p>
                    <p className="mt-1 text-xs text-foreground-600">热点追踪</p>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-primary-500">档案化</p>
                    <p className="mt-1 text-xs text-foreground-600">内容沉淀</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full bg-background-100">
            <div className="container-x py-14 md:py-16">
              <span className="eyebrow">
                <i className="ri-compasses-2-line" aria-hidden="true" />
                内容原则
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们坚持这样生产内容
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {principles.map((p) => (
                  <div key={p.title} className="card p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className={`${p.icon} text-xl`} aria-hidden="true" />
                    </span>
                    <h3 className="mt-4 font-label text-base font-semibold text-foreground-950">{p.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-700">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="container-x py-14 md:py-16">
            <span className="eyebrow">
              <i className="ri-chat-quote-line" aria-hidden="true" />
              读者口碑
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              追更的人这样说
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((v) => (
                <figure key={v.text} className="card flex flex-col p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-background-950">
                    <i className={`${v.icon} text-lg`} aria-hidden="true" />
                  </span>
                  <blockquote className="mt-4 flex-1 text-[15px] leading-8 text-foreground-800">“{v.text}”</blockquote>
                  <figcaption className="mt-4 text-sm font-medium text-foreground-950">{v.who}</figcaption>
                </figure>
              ))}
            </div>
          </section>

          <section className="w-full bg-accent-100/50">
            <div className="container-x flex flex-col items-start justify-between gap-8 py-12 md:py-16 lg:flex-row lg:items-center">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
                <p className="mt-3 max-w-xl text-[15px] leading-7 text-foreground-800">
                  欢迎投稿走红事件线索、提供建议或洽谈内容合作。我们会在收到后尽快与你联系。
                </p>
                <div className="mt-5 flex flex-col gap-2.5 text-sm text-foreground-700">
                  <p className="inline-flex items-center gap-2.5">
                    <i className="ri-mail-line text-primary-600" aria-hidden="true" />
                    contact@chiguabaowang.example
                  </p>
                  <p className="inline-flex items-center gap-2.5">
                    <i className="ri-question-line text-primary-600" aria-hidden="true" />
                    想了解订阅与更新频率？可先阅读常见问题或直接订阅资讯。
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <a href="mailto:contact@chiguabaowang.example" className="btn-primary cursor-pointer">
                  <i className="ri-mail-send-line" aria-hidden="true" />
                  发送邮件
                </a>
                <Link to="/#subscribe" className="btn-ghost cursor-pointer">
                  <i className="ri-mail-add-line" aria-hidden="true" />
                  订阅资讯
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}